import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken, Point, Rewards } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { useMemo } from 'react';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { calculateProjectedEarningsMonthly, calculateProjectedEarningsYearly } from '@/helpers/apy';
import { isVaultV2RateCapped } from '@/helpers/vaultV2RateCap';
import { useVaultV2Apy } from '@/hooks/useVaultV2Apy';
import type { VaultV2ActionPanelData } from '../../vaultV2ActionPanel.mapper';
interface UseVaultV2SummaryDataProps {
  vaultData: VaultV2ActionPanelData;
  vaultAsset: InterfaceToken | null | undefined;
  initialPosition: bigint | undefined;
  finalPosition: bigint | undefined;
}
export interface VaultV2SummaryData {
  apy: {
    raw?: bigint | null;
    net?: bigint | null | undefined;
    formatted: string;
    performanceFee?: bigint | null;
    managementFee?: bigint | null;
    maxRate?: bigint | null;
    maxApy?: number | null;
    apyBreakdown?: VaultV2ActionPanelData['apyBreakdown'];
    isRateCapped: boolean;
    includesDonations: boolean;
    points: Point[];
    rewards: Rewards[];
  };
  position: {
    initial: {
      raw?: bigint;
      formatted: string;
      detailed?: string;
    };
    final: {
      raw?: bigint;
      formatted?: string;
      detailed?: string;
    };
    showFinalValue: boolean;
  };
  projectedEarnings: {
    yearly: {
      initial: string;
      initialTooltip?: string;
      final?: string;
      finalTooltip?: string;
      showFinalValue: boolean;
    };
    monthly: {
      initial: string;
      initialTooltip?: string;
      final?: string;
      finalTooltip?: string;
      showFinalValue: boolean;
    };
  };
}
export const useVaultV2SummaryData = t0 => {
  const $ = _c(116);
  const {
    vaultData,
    vaultAsset,
    initialPosition,
    finalPosition
  } = t0;
  vaultAsset?.decimals;
  vaultAsset?.price;
  let t1;
  if ($[0] !== initialPosition || $[1] !== vaultAsset?.decimals || $[2] !== vaultAsset?.price) {
    t1 = formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, initialPosition)).trailingZero(!initialPosition).default("0.00").of(initialPosition, vaultAsset?.decimals);
    $[0] = initialPosition;
    $[1] = vaultAsset?.decimals;
    $[2] = vaultAsset?.price;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formattedInitialPosition = t1;
  let t2;
  if ($[4] !== initialPosition || $[5] !== vaultAsset?.decimals || $[6] !== vaultAsset?.price || $[7] !== vaultAsset?.symbol) {
    t2 = () => initialPosition && initialPosition > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, initialPosition)).trailingZero(false).unit(vaultAsset?.symbol).of(initialPosition, vaultAsset?.decimals) : undefined;
    $[4] = initialPosition;
    $[5] = vaultAsset?.decimals;
    $[6] = vaultAsset?.price;
    $[7] = vaultAsset?.symbol;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  vaultAsset?.decimals;
  vaultAsset?.price;
  vaultAsset?.symbol;
  let t3;
  if ($[9] !== t2) {
    t3 = t2();
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const formattedInitialPositionDetailed = t3;
  let t4;
  if ($[11] !== vaultData) {
    t4 = {
      vaultV2: vaultData
    };
    $[11] = vaultData;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    apy,
    netApy,
    performanceFee,
    managementFee,
    maxRate,
    points,
    rewards
  } = useVaultV2Apy(t4);
  vaultAsset?.decimals;
  vaultAsset?.price;
  let t5;
  if ($[13] !== finalPosition || $[14] !== vaultAsset?.decimals || $[15] !== vaultAsset?.price) {
    t5 = formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, finalPosition)).trailingZero(!finalPosition).of(finalPosition, vaultAsset?.decimals);
    $[13] = finalPosition;
    $[14] = vaultAsset?.decimals;
    $[15] = vaultAsset?.price;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const formattedFinalPosition = t5;
  let t6;
  if ($[17] !== finalPosition || $[18] !== vaultAsset?.decimals || $[19] !== vaultAsset?.price || $[20] !== vaultAsset?.symbol) {
    t6 = () => finalPosition && finalPosition > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(vaultAsset?.price, finalPosition)).trailingZero(false).unit(vaultAsset?.symbol).of(finalPosition, vaultAsset?.decimals) : undefined;
    $[17] = finalPosition;
    $[18] = vaultAsset?.decimals;
    $[19] = vaultAsset?.price;
    $[20] = vaultAsset?.symbol;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  vaultAsset?.decimals;
  vaultAsset?.price;
  vaultAsset?.symbol;
  let t7;
  if ($[22] !== t6) {
    t7 = t6();
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const formattedFinalPositionDetailed = t7;
  let t8;
  if ($[24] !== netApy) {
    t8 = formatters.percent.digits(2).default("-").unit("%").max(10).of(netApy, 18);
    $[24] = netApy;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const formattedNetApy = t8;
  let t9;
  if ($[26] !== initialPosition || $[27] !== netApy || $[28] !== vaultAsset) {
    const projectedEarningsAssetUsdYearly = calculateProjectedEarningsYearly(netApy, initialPosition, vaultAsset);
    t9 = formatters.short.digits(2).default("$0.00").unit("$").max(100000000).of(projectedEarningsAssetUsdYearly, 18);
    $[26] = initialPosition;
    $[27] = netApy;
    $[28] = vaultAsset;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const formattedProjectedEarningsYearlyInitial = t9;
  let t10;
  if ($[30] !== finalPosition || $[31] !== netApy || $[32] !== vaultAsset) {
    const projectedEarningsAssetUsdYearly_0 = calculateProjectedEarningsYearly(netApy, finalPosition, vaultAsset);
    t10 = formatters.short.digits(2).unit("$").max(100000000).of(projectedEarningsAssetUsdYearly_0, 18);
    $[30] = finalPosition;
    $[31] = netApy;
    $[32] = vaultAsset;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const formattedProjectedEarningsYearlyFinal = t10;
  let t11;
  if ($[34] !== finalPosition || $[35] !== netApy || $[36] !== vaultAsset) {
    const projectedEarningsAssetUsdYearly_1 = calculateProjectedEarningsYearly(netApy, finalPosition, vaultAsset);
    t11 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(projectedEarningsAssetUsdYearly_1, 18);
    $[34] = finalPosition;
    $[35] = netApy;
    $[36] = vaultAsset;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const detailedProjectedEarningsYearlyFinal = t11;
  let t12;
  if ($[38] !== initialPosition || $[39] !== netApy || $[40] !== vaultAsset) {
    const monthlyEarnings = calculateProjectedEarningsMonthly(netApy, initialPosition, vaultAsset);
    t12 = formatters.short.digits(2).default("$0.00").unit("$").max(100000000).of(monthlyEarnings, 18);
    $[38] = initialPosition;
    $[39] = netApy;
    $[40] = vaultAsset;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const formattedProjectedEarningsMonthlyInitial = t12;
  let t13;
  if ($[42] !== initialPosition || $[43] !== netApy || $[44] !== vaultAsset) {
    const monthlyEarnings_0 = calculateProjectedEarningsMonthly(netApy, initialPosition, vaultAsset);
    t13 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(monthlyEarnings_0, 18);
    $[42] = initialPosition;
    $[43] = netApy;
    $[44] = vaultAsset;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const detailedProjectedEarningsMonthlyInitial = t13;
  let t14;
  if ($[46] !== initialPosition || $[47] !== netApy || $[48] !== vaultAsset) {
    const yearlyEarnings = calculateProjectedEarningsYearly(netApy, initialPosition, vaultAsset);
    t14 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(yearlyEarnings, 18);
    $[46] = initialPosition;
    $[47] = netApy;
    $[48] = vaultAsset;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const detailedProjectedEarningsYearlyInitial = t14;
  let t15;
  if ($[50] !== finalPosition || $[51] !== netApy || $[52] !== vaultAsset) {
    const monthlyEarnings_1 = calculateProjectedEarningsMonthly(netApy, finalPosition, vaultAsset);
    t15 = formatters.short.digits(2).unit("$").max(100000000).of(monthlyEarnings_1, 18);
    $[50] = finalPosition;
    $[51] = netApy;
    $[52] = vaultAsset;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  const formattedProjectedEarningsMonthlyFinal = t15;
  let t16;
  if ($[54] !== finalPosition || $[55] !== netApy || $[56] !== vaultAsset) {
    const monthlyEarnings_2 = calculateProjectedEarningsMonthly(netApy, finalPosition, vaultAsset);
    t16 = formatters.commas.digits(2).unit("$").default("$0.00").max(100000000).of(monthlyEarnings_2, 18);
    $[54] = finalPosition;
    $[55] = netApy;
    $[56] = vaultAsset;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  const detailedProjectedEarningsMonthlyFinal = t16;
  let t17;
  if ($[58] !== finalPosition) {
    t17 = isDefined(finalPosition);
    $[58] = finalPosition;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  const showFinalValue = t17;
  let t18;
  if ($[60] !== formattedProjectedEarningsMonthlyFinal || $[61] !== showFinalValue) {
    t18 = showFinalValue && isDefined(formattedProjectedEarningsMonthlyFinal);
    $[60] = formattedProjectedEarningsMonthlyFinal;
    $[61] = showFinalValue;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const showMonthlyEarningsFinalValue = t18;
  let t19;
  if ($[63] !== formattedProjectedEarningsYearlyFinal || $[64] !== showFinalValue) {
    t19 = showFinalValue && isDefined(formattedProjectedEarningsYearlyFinal);
    $[63] = formattedProjectedEarningsYearlyFinal;
    $[64] = showFinalValue;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const showYearlyEarningsFinalValue = t19;
  const t20 = vaultData.apyBreakdown;
  const t21 = vaultData.maxApy;
  let t22;
  if ($[66] !== vaultData.apy || $[67] !== vaultData.maxApy) {
    t22 = isVaultV2RateCapped(vaultData.apy, vaultData.maxApy);
    $[66] = vaultData.apy;
    $[67] = vaultData.maxApy;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const t23 = vaultAsset?.chain.id;
  let t24;
  if ($[69] !== t23 || $[70] !== vaultData.address) {
    t24 = vaultIncludesDonations(t23, vaultData.address);
    $[69] = t23;
    $[70] = vaultData.address;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== apy || $[73] !== formattedNetApy || $[74] !== managementFee || $[75] !== maxRate || $[76] !== netApy || $[77] !== performanceFee || $[78] !== points || $[79] !== rewards || $[80] !== t22 || $[81] !== t24 || $[82] !== vaultData.apyBreakdown || $[83] !== vaultData.maxApy) {
    t25 = {
      raw: apy,
      net: netApy,
      formatted: formattedNetApy,
      performanceFee,
      managementFee,
      maxRate,
      apyBreakdown: t20,
      maxApy: t21,
      isRateCapped: t22,
      includesDonations: t24,
      points,
      rewards
    };
    $[72] = apy;
    $[73] = formattedNetApy;
    $[74] = managementFee;
    $[75] = maxRate;
    $[76] = netApy;
    $[77] = performanceFee;
    $[78] = points;
    $[79] = rewards;
    $[80] = t22;
    $[81] = t24;
    $[82] = vaultData.apyBreakdown;
    $[83] = vaultData.maxApy;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  let t26;
  if ($[85] !== formattedInitialPosition || $[86] !== formattedInitialPositionDetailed || $[87] !== initialPosition) {
    t26 = {
      raw: initialPosition,
      formatted: formattedInitialPosition,
      detailed: formattedInitialPositionDetailed
    };
    $[85] = formattedInitialPosition;
    $[86] = formattedInitialPositionDetailed;
    $[87] = initialPosition;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  let t27;
  if ($[89] !== finalPosition || $[90] !== formattedFinalPosition || $[91] !== formattedFinalPositionDetailed) {
    t27 = {
      raw: finalPosition,
      formatted: formattedFinalPosition,
      detailed: formattedFinalPositionDetailed
    };
    $[89] = finalPosition;
    $[90] = formattedFinalPosition;
    $[91] = formattedFinalPositionDetailed;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let t28;
  if ($[93] !== showFinalValue || $[94] !== t26 || $[95] !== t27) {
    t28 = {
      initial: t26,
      final: t27,
      showFinalValue
    };
    $[93] = showFinalValue;
    $[94] = t26;
    $[95] = t27;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== detailedProjectedEarningsYearlyFinal || $[98] !== detailedProjectedEarningsYearlyInitial || $[99] !== formattedProjectedEarningsYearlyFinal || $[100] !== formattedProjectedEarningsYearlyInitial || $[101] !== showYearlyEarningsFinalValue) {
    t29 = {
      initial: formattedProjectedEarningsYearlyInitial,
      initialTooltip: detailedProjectedEarningsYearlyInitial,
      final: formattedProjectedEarningsYearlyFinal,
      finalTooltip: detailedProjectedEarningsYearlyFinal,
      showFinalValue: showYearlyEarningsFinalValue
    };
    $[97] = detailedProjectedEarningsYearlyFinal;
    $[98] = detailedProjectedEarningsYearlyInitial;
    $[99] = formattedProjectedEarningsYearlyFinal;
    $[100] = formattedProjectedEarningsYearlyInitial;
    $[101] = showYearlyEarningsFinalValue;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  let t30;
  if ($[103] !== detailedProjectedEarningsMonthlyFinal || $[104] !== detailedProjectedEarningsMonthlyInitial || $[105] !== formattedProjectedEarningsMonthlyFinal || $[106] !== formattedProjectedEarningsMonthlyInitial || $[107] !== showMonthlyEarningsFinalValue) {
    t30 = {
      initial: formattedProjectedEarningsMonthlyInitial,
      initialTooltip: detailedProjectedEarningsMonthlyInitial,
      final: formattedProjectedEarningsMonthlyFinal,
      finalTooltip: detailedProjectedEarningsMonthlyFinal,
      showFinalValue: showMonthlyEarningsFinalValue
    };
    $[103] = detailedProjectedEarningsMonthlyFinal;
    $[104] = detailedProjectedEarningsMonthlyInitial;
    $[105] = formattedProjectedEarningsMonthlyFinal;
    $[106] = formattedProjectedEarningsMonthlyInitial;
    $[107] = showMonthlyEarningsFinalValue;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  let t31;
  if ($[109] !== t29 || $[110] !== t30) {
    t31 = {
      yearly: t29,
      monthly: t30
    };
    $[109] = t29;
    $[110] = t30;
    $[111] = t31;
  } else {
    t31 = $[111];
  }
  let t32;
  if ($[112] !== t25 || $[113] !== t28 || $[114] !== t31) {
    t32 = {
      apy: t25,
      position: t28,
      projectedEarnings: t31
    };
    $[112] = t25;
    $[113] = t28;
    $[114] = t31;
    $[115] = t32;
  } else {
    t32 = $[115];
  }
  return t32;
};